import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Inbox, Loader2, ExternalLink, AlertTriangle, UserCheck, CalendarPlus, Plus, Building2,
  Paperclip, Upload, Download, Link2, UserPlus, Trash2, RefreshCw,
} from "lucide-react";
import BackfillDialog from "@/components/coda/BackfillDialog";
import WorkspaceTabs from "@/components/coda/WorkspaceTabs";
import {
  ASSIGNMENT_STATES, ASSIGNMENT_PRIORITIES, CUSTOMER_TYPES, OPP_TYPES,
  assignmentTone, priorityTone, kindLabel, workLabel, fmtDateTime, fmtDate, fmtSize,
} from "@/lib/assignments";
import { buildFullAddress, isAddressComplete } from "@/lib/calendar";
import NewLeadDialog from "@/components/leads/NewLeadDialog";

export default function CodaLavoriPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [filters, setFilters] = useState({
    status: params.get("status") || "", origin: "", dest: "", type: "", taken: "", mine: "", search: "",
  });
  const [detailId, setDetailId] = useState(params.get("open") || null);
  const [showNew, setShowNew] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [showBackfill, setShowBackfill] = useState(null); // null | "appointments" | "opportunities"
  const isOperator = user?.role === "operatore";
  const isCross = user?.role === "admin" || user?.role === "backoffice";

  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (filters.status) p.status = filters.status;
      if (filters.origin) p.origin_office_id = filters.origin;
      if (filters.dest) p.dest_office_id = filters.dest;
      if (filters.type) p.opportunity_type = filters.type;
      if (filters.taken) p.taken_by = filters.taken;
      if (filters.mine) p.assigned_by = filters.mine;
      if (filters.search) p.search = filters.search;
      const { data } = await api.get("/assignments", { params: p });
      setRows(data);
    } catch (e) { setRows([]); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => { setDetailId(id); setParams({ open: id }); };
  const closeDetail = () => { setDetailId(null); setParams({}); };

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="coda-lavori-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><Inbox className="h-6 w-6 text-brand" /> Coda Lavori / Lead</h1>
          <p className="mt-1 text-sm text-slate-500">Assegnazione e presa in carico di lead e lavori tra sedi, Direzione e Back Office.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" data-testid="backfill-button" onClick={() => setShowBackfill("appointments")} className="gap-1.5">
                <RefreshCw className="h-4 w-4" /> Appuntamenti senza pratica
              </Button>
              <Button variant="outline" data-testid="backfill-opps-button" onClick={() => setShowBackfill("opportunities")} className="gap-1.5">
                <RefreshCw className="h-4 w-4" /> Lead senza pratica
              </Button>
              <Button data-testid="new-work-button" onClick={() => setShowNew(true)} className="gap-1.5">
                <Plus className="h-4 w-4" /> Nuovo lavoro
              </Button>
            </>
          )}
          {(isOperator || isCross) && (
            <Button data-testid="new-lead-button" onClick={() => setShowNewLead(true)} className="gap-1.5" variant={isAdmin ? "outline" : "default"}>
              <Plus className="h-4 w-4" /> Nuovo Lead
            </Button>
          )}
        </div>
      </div>

      {/* Filtri rapidi */}
      <div className="ds-card flex flex-wrap items-end gap-3 p-4">
        <Input data-testid="coda-search" placeholder="Cerca cliente / contatto / titolo…" className="w-[220px]"
          value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
        <FilterSelect testid="filter-status" value={filters.status} placeholder="Stato" options={ASSIGNMENT_STATES}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))} allLabel="Tutti gli stati" />
        <FilterSelect testid="filter-origin" value={filters.origin} placeholder="Sede origine"
          options={offices.map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, origin: v }))} allLabel="Tutte le origini" />
        <FilterSelect testid="filter-dest" value={filters.dest} placeholder="Sede destinataria"
          options={offices.map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, dest: v }))} allLabel="Tutte le destinazioni" />
        <FilterSelect testid="filter-type" value={filters.type} placeholder="Tipologia" options={OPP_TYPES}
          onChange={(v) => setFilters((f) => ({ ...f, type: v }))} allLabel="Tutte le tipologie" />
        <FilterSelect testid="filter-taken" value={filters.taken} placeholder="Presa in carico"
          options={[{ value: "none", label: "Da prendere" }]} onChange={(v) => setFilters((f) => ({ ...f, taken: v }))}
          allLabel="Tutte" />
        <FilterSelect testid="filter-mine" value={filters.mine} placeholder="Inseriti da"
          options={[{ value: "me", label: "Inseriti da me" }]} onChange={(v) => setFilters((f) => ({ ...f, mine: v }))}
          allLabel="Inseriti da chiunque" />
      </div>

      {/* Tabella coda */}
      <div className="ds-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500"><Inbox className="h-8 w-8 text-slate-300" /> Nessun lead/lavoro con questi filtri.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Titolo / Cliente</th>
                  <th className="px-3 py-2.5">Origine → Destinazione</th>
                  <th className="px-3 py-2.5">Tipologia</th>
                  <th className="px-3 py-2.5">Priorità</th>
                  <th className="px-3 py-2.5">Stato</th>
                  <th className="px-3 py-2.5">Preso in carico da</th>
                  <th className="px-3 py-2.5">Inserito da / il</th>
                  <th className="px-3 py-2.5">Scadenza</th>
                  <th className="px-3 py-2.5 text-center">Allegati</th>
                  <th className="px-3 py-2.5">Aggiornato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((a) => (
                  <tr key={a.id} onClick={() => openDetail(a.id)} data-testid={`coda-row-${a.id}`}
                    className={`cursor-pointer hover:bg-brand/5 ${a.status === "Da prendere in carico" ? "border-l-4 border-l-warning" : "border-l-4 border-l-transparent"}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{a.title || workLabel(a)}</div>
                      <div className="text-xs text-slate-500">
                        {a.customer_id
                          ? `${a.customer_name || ""}${a.customer_phone ? ` · ${a.customer_phone}` : ""}`.trim() || "—"
                          : (a.contact_name
                            ? `${a.contact_name}${a.contact_phone ? ` · ${a.contact_phone}` : ""}`
                            : <span className="italic text-slate-400">Lavoro libero</span>)} · {kindLabel(a.kind)}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{a.origin_office_name || "—"}</span>
                      <span className="mx-1 text-slate-400">→</span>
                      <span>{a.dest_office_name || "—"}</span>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{a.opportunity_type || "—"}</td>
                    <td className="px-3 py-3">
                      {a.priority ? <StatusBadge type={priorityTone(a.priority)}>{a.priority}</StatusBadge> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <StatusBadge type={assignmentTone(a.status)}>{a.status}</StatusBadge>
                        {a.is_stale && (
                          <span data-testid={`stale-${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-danger" title="Fermo da oltre 48 ore">
                            <AlertTriangle className="h-3 w-3" /> fermo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{a.taken_by_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-3 text-xs text-slate-600" data-testid={`coda-created-by-${a.id}`}>
                      <div className="font-medium text-slate-700">{a.assigned_by_name || "—"}</div>
                      <div className="text-slate-400">{fmtDateTime(a.created_at)}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{a.due_date ? fmtDate(a.due_date) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-3 text-center">
                      {a.attachments_count > 0
                        ? <span className="inline-flex items-center gap-1 text-slate-600" data-testid={`attach-count-${a.id}`}><Paperclip className="h-3.5 w-3.5" />{a.attachments_count}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500">{fmtDateTime(a.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailId && (
        <AssignmentDetail id={detailId} onClose={closeDetail} onChanged={load} offices={offices} navigate={navigate} />
      )}
      {showNew && (
        <NewWorkDialog open={showNew} onOpenChange={setShowNew} offices={offices}
          onCreated={() => { setShowNew(false); load(); }} />
      )}
      {showBackfill && <BackfillDialog open kind={showBackfill} onOpenChange={(v) => !v && setShowBackfill(null)} onDone={load} />}
      {showNewLead && (
        <NewLeadDialog open={showNewLead} onOpenChange={setShowNewLead} cross={isCross}
          onSaved={(opp) => { setShowNewLead(false); load(); if (opp?.assignment_id) openDetail(opp.assignment_id); }} />
      )}
    </div>
  );
}

function FilterSelect({ testid, value, placeholder, options, onChange, allLabel }) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="w-[170px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="all">{allLabel}</SelectItem>
        {norm.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-slate-700">{value || "—"}</dd>
    </div>
  );
}

// --------------------------------------------------------------------------- DETTAGLIO
function AssignmentDetail({ id, onClose, onChanged, offices, navigate }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const [hardDeleting, setHardDeleting] = useState(false);

  const hardDelete = async () => {
    setHardDeleting(true);
    try {
      await api.delete(`/assignments/${id}/hard`, { params: { confirm: true } });
      toast.success("Lavoro eliminato definitivamente");
      setConfirmHardDelete(false);
      onChanged();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setHardDeleting(false); }
  };
  const [busy, setBusy] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [showAppt, setShowAppt] = useState(false);
  const [showCreateCust, setShowCreateCust] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assignments/${id}`);
      setData(data); setNewStatus(data.status);
    } catch (e) { toast.error("Impossibile aprire la voce"); onClose(); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const take = () => act(() => api.post(`/assignments/${id}/take`), "Preso in carico");
  const changeStatus = () => act(() => api.patch(`/assignments/${id}/status`, { status: newStatus }), "Stato aggiornato");
  const reassign = (officeId) => act(() => api.patch(`/assignments/${id}/reassign`, { dest_office_id: officeId }), "Riassegnato");
  const linkCustomer = (customerId) => act(() => api.post(`/assignments/${id}/link-customer`, { customer_id: customerId }), "Cliente collegato");

  const p = data?.perms || {};
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="assignment-detail">
        <SheetHeader><SheetTitle>Dettaglio lead / lavoro</SheetTitle></SheetHeader>
        {!data ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="text-base font-semibold text-slate-800" data-testid="detail-title">{workLabel(data)}</span>
                <StatusBadge type={assignmentTone(data.status)}>{data.status}</StatusBadge>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <span>{kindLabel(data.kind)}</span>
                {data.priority && <StatusBadge type={priorityTone(data.priority)}>{data.priority}</StatusBadge>}
                {!data.customer_id && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">Lavoro libero</span>}
              </div>
              {data.description && <p className="mt-2 whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="detail-description">{data.description}</p>}
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <Field label="Sede origine" value={data.origin_office_name} />
                <Field label="Sede destinataria" value={data.dest_office_name} />
                <Field label="Tipologia" value={data.opportunity_type} />
                <Field label="Scadenza" value={data.due_date ? fmtDate(data.due_date) : "—"} />
                <Field label="Assegnato da" value={data.assigned_by_name} />
                <Field label="Preso in carico da" value={data.taken_by_name} />
                <Field label="Creato" value={fmtDateTime(data.created_at)} />
                <Field label="Aggiornato" value={fmtDateTime(data.updated_at)} />
              </dl>
              {/* Contatto (lavoro senza cliente) */}
              {(!data.customer_id && (data.contact_name || data.contact_phone || data.contact_email)) && (
                <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid="detail-contact">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Contatto</div>
                  <div className="text-slate-700">{data.contact_name || "—"}</div>
                  <div className="text-slate-500">{data.contact_phone || "—"}{data.contact_email ? ` · ${data.contact_email}` : ""}</div>
                </div>
              )}
              {data.appointment && (
                <div className="mt-2 rounded-md bg-brand/5 px-3 py-2 text-sm text-brand" data-testid="assignment-appointment">
                  <CalendarPlus className="mr-1 inline h-4 w-4" /> Appuntamento: {data.appointment.when} ({data.appointment.status})
                </div>
              )}
              {data.notes && <p className="mt-2 text-sm text-slate-600">{data.notes}</p>}
            </div>

            {/* Allegati */}
            <AttachmentsPanel assignmentId={id} canManage={p.can_manage} onChanged={() => { load(); onChanged(); }} />

            {/* Note, Attività, Sopralluoghi, Storico */}
            <WorkspaceTabs assignment={data} canManage={p.can_manage} onChanged={() => { load(); onChanged(); }} />

            {/* Collega / crea cliente (solo se lavoro senza cliente) */}
            {p.can_link_customer && (
              <div className="rounded-lg border border-border p-3" data-testid="link-customer-panel">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Link2 className="h-4 w-4" /> Collega a cliente</h3>
                <CustomerPicker onPick={(c) => linkCustomer(c.id)} disabled={busy} />
                <Button variant="outline" className="mt-2 w-full gap-1.5" onClick={() => setShowCreateCust(true)} data-testid="create-customer-from-work">
                  <UserPlus className="h-4 w-4" /> Crea cliente da questo lavoro
                </Button>
              </div>
            )}

            {/* Azioni */}
            <div className="space-y-3">
              {p.can_take && (
                <Button onClick={take} disabled={busy} className="w-full gap-1.5" data-testid="assignment-take">
                  <UserCheck className="h-4 w-4" /> Prendi in carico
                </Button>
              )}
              {!p.can_take && data.taken_by_name && data.status !== "Da prendere in carico" && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Già preso in carico da <b>{data.taken_by_name}</b>.
                </div>
              )}
              {p.take_msg && !p.can_take && !data.taken_by_name && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">{p.take_msg}</div>
              )}

              {p.can_manage && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-slate-500">Cambia stato</label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="assignment-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {ASSIGNMENT_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="secondary" disabled={busy || newStatus === data.status} onClick={changeStatus} data-testid="assignment-status-save">Aggiorna</Button>
                </div>
              )}

              {p.can_manage && user?.can_manage_appointments && !data.appointment && data.customer_id && (
                <Button variant="outline" className="w-full gap-1.5" onClick={() => setShowAppt(true)} data-testid="assignment-create-appt">
                  <CalendarPlus className="h-4 w-4" /> Crea appuntamento
                </Button>
              )}
              {p.can_manage && !data.appointment && !data.customer_id && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">Collega o crea un cliente per fissare un appuntamento.</div>
              )}

              {p.can_reassign && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Riassegna a sede</label>
                  <Select value="" onValueChange={reassign}>
                    <SelectTrigger data-testid="assignment-reassign"><SelectValue placeholder="Scegli sede…" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {offices.filter((o) => o.id !== data.dest_office_id).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {data.customer_id && (
                <Button variant="ghost" className="w-full gap-1.5" onClick={() => navigate(`/clienti/${data.customer_id}`)} data-testid="assignment-open-customer">
                  <ExternalLink className="h-4 w-4" /> Apri scheda cliente
                </Button>
              )}
            </div>
          </div>
        )}
        {showAppt && data && (
          <ApptDialog open={showAppt} onOpenChange={setShowAppt} assignment={data}
            onCreated={() => { setShowAppt(false); load(); onChanged(); }} />
        )}
        {data && user?.role === "admin" && data.status === "Annullato" && (
          <div className="mt-6 border-t border-border pt-4">
            <Button variant="destructive" size="sm" className="w-full gap-1.5" onClick={() => setConfirmHardDelete(true)} disabled={hardDeleting} data-testid="assignment-hard-delete">
              {hardDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4" /> Elimina definitivamente</>}
            </Button>
            <p className="mt-1 text-[11px] text-slate-500">Solo Direzione · solo lavori Annullati. Cliente, opportunità e appuntamenti non vengono toccati.</p>
          </div>
        )}
        <AlertDialog open={confirmHardDelete} onOpenChange={setConfirmHardDelete}>
          <AlertDialogContent data-testid="assignment-hard-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminazione definitiva</AlertDialogTitle>
              <AlertDialogDescription>Sei sicuro di voler eliminare definitivamente questo lavoro? L'operazione non è reversibile.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="assignment-hard-delete-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={hardDelete} className="bg-danger text-white hover:bg-danger/90" data-testid="assignment-hard-delete-ok">Elimina definitivamente</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {showCreateCust && data && (
          <CreateCustomerDialog open={showCreateCust} onOpenChange={setShowCreateCust} assignment={data} offices={offices}
            onDone={() => { setShowCreateCust(false); load(); onChanged(); }}
            onLinkExisting={(cid) => { setShowCreateCust(false); linkCustomer(cid); }} />
        )}
      </SheetContent>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- ALLEGATI
function AttachmentsPanel({ assignmentId, canManage, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/assignments/${assignmentId}/attachments`); setDocs(data); }
    catch (e) { setDocs([]); } finally { setLoading(false); }
  }, [assignmentId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/assignments/${assignmentId}/attachments`, fd);
      toast.success("Allegato caricato");
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload non riuscito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/assignments/${assignmentId}/attachments/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = doc.filename;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download non riuscito"); }
  };

  return (
    <div className="rounded-lg border border-border p-3" data-testid="attachments-panel">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Paperclip className="h-4 w-4" /> Allegati</h3>
        {canManage && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" data-testid="attachment-input"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={upload} />
            <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="attachment-upload-button">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Carica
            </Button>
          </>
        )}
      </div>
      {loading ? (
        <div className="py-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-brand" /></div>
      ) : docs.length === 0 ? (
        <p className="text-xs text-slate-400">Nessun allegato.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="attachments-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-2.5 py-1.5 text-sm">
              <button onClick={() => download(d)} data-testid={`attachment-${d.id}`} className="flex min-w-0 items-center gap-2 text-left text-slate-700 hover:text-brand">
                <Download className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{d.filename}</span>
              </button>
              <span className="shrink-0 text-[11px] text-slate-400">{fmtSize(d.file_size)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- CUSTOMER PICKER (ricerca)
function CustomerPicker({ onPick, disabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/customers/search", { params: { q } }); setResults(data); }
      catch (e) { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <Input placeholder="Cerca cliente…" value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} data-testid="link-customer-search" />
      {results.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border">
          {results.map((c) => (
            <button key={c.id} onClick={() => { onPick(c); setResults([]); setQ(""); }} data-testid={`link-customer-${c.id}`}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-brand/5">
              {c.display_name} · {c.phone_primary || "—"} {c.office_name ? `· ${c.office_name}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- APPUNTAMENTO (richiede cliente)
function ApptDialog({ open, onOpenChange, assignment, onCreated }) {
  const [form, setForm] = useState({ appt_date: "", appt_time: "", type: "Sopralluogo", notes: "", assignee_consultant_id: "" });
  const [consultants, setConsultants] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState("");
  useEffect(() => {
    api.get("/consultants").then(({ data }) => setConsultants(data)).catch(() => {});
    if (assignment?.customer_id) api.get(`/customers/${assignment.customer_id}`).then(({ data }) => setCustomer(data)).catch(() => {});
  }, [assignment]);
  const fullAddr = buildFullAddress(customer);
  const submit = async (force = false) => {
    if (!form.appt_date) { toast.error("Indica la data"); return; }
    setBusy(true); setConflict("");
    try {
      await api.post("/appointments", {
        customer_id: assignment.customer_id, opportunity_id: assignment.opportunity_id || undefined,
        appt_date: form.appt_date, appt_time: form.appt_time || undefined, type: form.type,
        notes: form.notes || undefined, assignment_id: assignment.id, address: fullAddr || undefined,
        assignee_consultant_id: form.assignee_consultant_id || undefined, force,
      });
      toast.success("Appuntamento creato");
      onCreated();
    } catch (e) {
      const d = formatApiErrorDetail(e.response?.data?.detail) || "Errore";
      if (e.response?.status === 409 && String(d).includes("Google")) setConflict(d);
      else toast.error(d);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="appt-dialog">
        <DialogHeader><DialogTitle>Crea appuntamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid="appt-customer-info">
            <div className="font-semibold text-slate-800">{assignment.customer_name} · {assignment.customer_phone || "—"}</div>
            <div className="text-slate-600">{fullAddr || "Indirizzo non disponibile"}</div>
            {!isAddressComplete(customer) && <div className="mt-0.5 text-xs text-warning" data-testid="appt-address-incomplete">⚠ Indirizzo incompleto: verifica via, civico e comune.</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Data *</label>
              <Input type="date" value={form.appt_date} onChange={(e) => setForm((f) => ({ ...f, appt_date: e.target.value }))} data-testid="appt-date" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Ora</label>
              <Input type="time" value={form.appt_time} onChange={(e) => setForm((f) => ({ ...f, appt_time: e.target.value }))} data-testid="appt-time" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Appuntamento assegnato a</label>
            <Select value={form.assignee_consultant_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, assignee_consultant_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="appt-assignee"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">Nessuno</SelectItem>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Tipo</label>
            <Input value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="appt-type" />
          </div>
          <Textarea placeholder="Note (opz.)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="appt-notes" />
          {conflict && (
            <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning" data-testid="appt-google-conflict">
              {conflict}
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => submit(true)} data-testid="appt-force-submit">Conferma comunque</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => submit()} disabled={busy} data-testid="appt-submit">{busy ? "Creazione…" : "Crea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------- NUOVO LAVORO (Direzione)
function NewWorkDialog({ open, onOpenChange, offices, onCreated }) {
  const [customer, setCustomer] = useState(null);
  const [destOffice, setDestOffice] = useState("");
  const [form, setForm] = useState({
    title: "", description: "", priority: "", due_date: "", type: "", notes: "",
    contact_first_name: "", contact_last_name: "", contact_phone: "", contact_email: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!destOffice) { toast.error("Seleziona la sede destinataria"); return; }
    if (!customer && !form.title.trim()) { toast.error("Per un lavoro senza cliente il Titolo è obbligatorio"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("dest_office_id", destOffice);
      if (form.title.trim()) fd.append("title", form.title.trim());
      if (form.description.trim()) fd.append("description", form.description.trim());
      if (customer) fd.append("customer_id", customer.id);
      if (form.priority) fd.append("priority", form.priority);
      if (form.due_date) fd.append("due_date", form.due_date);
      if (form.type) fd.append("opportunity_type", form.type);
      if (form.notes.trim()) fd.append("notes", form.notes.trim());
      if (!customer) {
        if (form.contact_first_name.trim()) fd.append("contact_first_name", form.contact_first_name.trim());
        if (form.contact_last_name.trim()) fd.append("contact_last_name", form.contact_last_name.trim());
        if (form.contact_phone.trim()) fd.append("contact_phone", form.contact_phone.trim());
        if (form.contact_email.trim()) fd.append("contact_email", form.contact_email.trim());
      }
      files.forEach((f) => fd.append("files", f));
      await api.post("/assignments", fd);
      toast.success("Lavoro creato e assegnato");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="new-work-dialog">
        <DialogHeader><DialogTitle>Nuovo lavoro → sede</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Titolo lavoro {customer ? "" : "*"}</label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Es. Sopralluogo fotovoltaico" data-testid="work-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Descrizione / istruzioni operative</label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="work-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Sede destinataria *</label>
              <Select value={destOffice} onValueChange={setDestOffice}>
                <SelectTrigger data-testid="work-dest-office"><SelectValue placeholder="Scegli sede…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Priorità</label>
              <Select value={form.priority || "none"} onValueChange={(v) => set("priority", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="work-priority"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">—</SelectItem>
                  {ASSIGNMENT_PRIORITIES.map((pr) => <SelectItem key={pr} value={pr}>{pr}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Scadenza</label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="work-due-date" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Tipologia</label>
              <Select value={form.type || "none"} onValueChange={(v) => set("type", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="work-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">—</SelectItem>
                  {OPP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border border-border p-3">
            <label className="mb-1 block text-xs font-medium text-slate-500">Cliente CRM (facoltativo)</label>
            {customer ? (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span>{customer.display_name} · {customer.phone_primary || "—"}</span>
                <button className="text-xs text-brand hover:underline" onClick={() => setCustomer(null)} data-testid="work-clear-customer">Rimuovi</button>
              </div>
            ) : (
              <>
                <CustomerPicker onPick={setCustomer} />
                <p className="mt-1 text-[11px] text-slate-400">Se non selezioni un cliente, compila i dati contatto qui sotto (facoltativi).</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Input placeholder="Nome contatto" value={form.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} data-testid="work-contact-first" />
                  <Input placeholder="Cognome contatto" value={form.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} data-testid="work-contact-last" />
                  <Input placeholder="Telefono" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="work-contact-phone" />
                  <Input placeholder="Email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="work-contact-email" />
                </div>
              </>
            )}
          </div>

          <Textarea placeholder="Note (opz.)" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="work-notes" />

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Allegati (PDF, JPG, PNG, DOC, DOCX, XLS, XLSX)</label>
            <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="work-files" className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-3 file:py-1.5 file:text-sm file:text-brand" />
            {files.length > 0 && <p className="mt-1 text-xs text-slate-500" data-testid="work-files-count">{files.length} file selezionato/i</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="work-submit">{busy ? "Creazione…" : "Crea e assegna"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------- CREA CLIENTE DAL LAVORO
function CreateCustomerDialog({ open, onOpenChange, assignment, offices, onDone, onLinkExisting }) {
  const [form, setForm] = useState({
    type: "Privato",
    first_name: assignment?.contact_first_name || "",
    last_name: assignment?.contact_last_name || "",
    company_name: "",
    phone_primary: assignment?.contact_phone || "",
    email: assignment?.contact_email || "",
    office_id: assignment?.dest_office_id || "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPrivato = form.type === "Privato";

  const submit = async () => {
    setBusy(true); setDup(null);
    try {
      await api.post(`/assignments/${assignment.id}/create-customer`, {
        type: form.type,
        first_name: isPrivato ? form.first_name || undefined : undefined,
        last_name: isPrivato ? form.last_name || undefined : undefined,
        company_name: !isPrivato ? form.company_name || undefined : undefined,
        phone_primary: form.phone_primary || undefined,
        email: form.email || undefined,
        office_id: form.office_id || undefined,
        notes: form.notes || undefined,
      });
      toast.success("Cliente creato e collegato");
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.existing) { setDup(detail.existing); }
      else toast.error(formatApiErrorDetail(detail) || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white" data-testid="create-customer-dialog">
        <DialogHeader><DialogTitle>Crea cliente da questo lavoro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Tipologia</label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="cc-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isPrivato ? (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Nome *" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="cc-first" />
              <Input placeholder="Cognome *" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="cc-last" />
            </div>
          ) : (
            <Input placeholder={form.type === "Azienda" ? "Ragione sociale *" : "Denominazione condominio *"} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="cc-company" />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Telefono" value={form.phone_primary} onChange={(e) => set("phone_primary", e.target.value)} data-testid="cc-phone" />
            <Input placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cc-email" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Sede</label>
            <Select value={form.office_id || "none"} onValueChange={(v) => set("office_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="cc-office"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Note (opz.)" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="cc-notes" />
          {dup && (
            <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm" data-testid="cc-duplicate">
              <div className="font-semibold text-slate-800">Possibile cliente già presente</div>
              <div className="text-slate-600">{dup.display_name} · {dup.phone_primary || "—"} {dup.office_name ? `· ${dup.office_name}` : ""}</div>
              <Button size="sm" variant="outline" className="mt-2 w-full gap-1.5" onClick={() => onLinkExisting(dup.id)} data-testid="cc-link-existing">
                <Link2 className="h-4 w-4" /> Collega a questo cliente
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="cc-submit">{busy ? "Creazione…" : "Crea e collega"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
